import type { ReactNode } from "react";

import { CarrierLogo } from "@/components/common/CarrierLogo";
import { PortAreaMatchBadge } from "@/components/common/PortAreaMatch";
import { Badge } from "@/components/ui/badge";
import { formatRouteLabel } from "@/lib/formatPort";
import {
  classifyAreaMatch,
  type AreaMatcher,
} from "@/lib/portAreaMatchUtils";
import { formatDateTimePlain } from "@/lib/weekDates";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import type { ScheduleLegResponse, ScheduleResponse } from "@/types/api";
import { formatBookingDate } from "@/types/booking";

import {
  collectScheduleCutoffs,
  formatScheduleLegPort,
  formatScheduleTransitDays,
  scheduleCarrierCode,
  schedulePrimaryVessel,
  scheduleSailingEta,
  scheduleSailingEtd,
  scheduleSailingTransitDays,
  sortedScheduleLegs,
} from "./scheduleSailingDisplay";
import { normalizeCutoffName } from "@/lib/scheduleCutoffs";

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex justify-between gap-4 py-1.5">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium tabular-nums">{children}</dd>
    </div>
  );
}

const EMPTY_AREA_MATCHER: AreaMatcher = {
  hasExpansion: false,
  requestedSet: new Set(),
  memberToRequested: new Map(),
  expansions: [],
};

/** Read-only sailing detail — used in dialogs and the booking matched-schedule rail. */
export function ScheduleSailingDetailPanel({
  sailing,
  areaMatcher = EMPTY_AREA_MATCHER,
  matchCaption,
}: {
  sailing: ScheduleResponse;
  areaMatcher?: AreaMatcher;
  /** e.g. why this schedule is linked to the booking. */
  matchCaption?: string | null;
}) {
  const { preferences } = useUserPreferences();
  const showLocodes = preferences.showLocodes;

  const etd = scheduleSailingEtd(sailing);
  const eta = scheduleSailingEta(sailing);
  const transitDays = scheduleSailingTransitDays(sailing);
  const route = formatRouteLabel(
    {
      locode: sailing.origin_locode ?? "",
      name: sailing.origin_port_name,
    },
    {
      locode: sailing.dest_locode ?? "",
      name: sailing.dest_port_name,
    },
    showLocodes,
  );
  const originMatch = classifyAreaMatch(areaMatcher, sailing.origin_locode);
  const showOriginMatch = areaMatcher.hasExpansion;
  const allCutoffs = collectScheduleCutoffs(sailing);
  const legs = sortedScheduleLegs(sailing);
  const primaryVessel = schedulePrimaryVessel(sailing);

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <CarrierLogo carrier={scheduleCarrierCode(sailing)} bold />
          <Badge variant={sailing.direct ? "default" : "info"}>
            {sailing.direct
              ? "Direct"
              : `Transshipment · ${sailing.legs.length} legs`}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{route}</p>
        {primaryVessel ? (
          <p className="text-sm font-medium">{primaryVessel}</p>
        ) : null}
        {matchCaption?.trim() ? (
          <p className="text-xs text-muted-foreground">{matchCaption.trim()}</p>
        ) : null}
      </div>

      <dl className="rounded-lg border border-border/50 bg-background/40 px-3 py-1 text-sm">
        <DetailRow label="ETD">{formatBookingDate(etd ?? undefined)}</DetailRow>
        <DetailRow label="ETA">{formatBookingDate(eta ?? undefined)}</DetailRow>
        {transitDays != null && (
          <DetailRow label="Transit time">
            {formatScheduleTransitDays(transitDays)}
          </DetailRow>
        )}
        {showOriginMatch && (
          <div className="flex justify-between gap-4 border-t border-border/40 py-1.5">
            <dt className="shrink-0 text-muted-foreground">Origin match</dt>
            <dd className="text-right">
              {originMatch.kind === "area" ? (
                <PortAreaMatchBadge
                  locode={sailing.origin_locode}
                  matcher={areaMatcher}
                />
              ) : originMatch.kind === "exact" ? (
                <span className="font-medium">Exact port</span>
              ) : (
                <span className="text-muted-foreground">Other port</span>
              )}
            </dd>
          </div>
        )}
      </dl>

      {legs.length > 0 && (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Voyage legs
          </h3>
          <ol className="flex flex-col gap-2">
            {legs.map((leg: ScheduleLegResponse) => (
              <li
                key={leg.leg_order}
                className="rounded-lg border border-border/50 bg-background/40 px-3 py-2.5"
              >
                <p className="text-sm font-medium">
                  Leg {leg.leg_order}
                  {leg.vessel_name?.trim()
                    ? ` · ${leg.vessel_name.trim()}`
                    : ""}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {formatScheduleLegPort(
                    leg.departure_locode,
                    leg.departure_port_name,
                    showLocodes,
                  )}{" "}
                  {formatBookingDate(
                    leg.departure_estimated_date?.slice(0, 10),
                  )}{" "}
                  →{" "}
                  {formatScheduleLegPort(
                    leg.arrival_locode,
                    leg.arrival_port_name,
                    showLocodes,
                  )}{" "}
                  {formatBookingDate(leg.arrival_estimated_date?.slice(0, 10))}
                </p>
              </li>
            ))}
          </ol>
        </section>
      )}

      {(allCutoffs.length > 0 || sailing.cargo_cutoff) && (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Cutoffs
          </h3>
          <ul className="rounded-lg border border-border/50 bg-background/40 px-3 py-1 text-sm">
            {sailing.cargo_cutoff && (
              <li className="flex justify-between gap-4 py-1.5">
                <span className="text-muted-foreground">Cargo cutoff</span>
                <span className="tabular-nums">
                  {formatDateTimePlain(sailing.cargo_cutoff)}
                </span>
              </li>
            )}
            {allCutoffs.map((c, i) => (
              <li
                key={`${c.name ?? "cutoff"}-${c.date_time}-${i}`}
                className="flex justify-between gap-4 py-1.5"
              >
                <span className="text-muted-foreground">
                  {normalizeCutoffName(c.name) || "Cutoff"}
                </span>
                <span className="tabular-nums">
                  {formatDateTimePlain(c.date_time!)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
